<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-card class="ui-module-card" color="primary">
          <v-card-title>
            <div class="text-h5 font-weight-bold">Licencias Oficiales Colectivas</div>
          </v-card-title>
          <v-card-subtitle>
            Administre las Licencias Oficiales Colectivas a las que el personal se encuentra inscrito.
          </v-card-subtitle>
          <v-card-text>
            <v-tabs v-model="activeTab" color="secondary">
              <v-tab>LOCs</v-tab>
              <v-tab>Remesas</v-tab>
            </v-tabs>

            <v-tabs-items v-model="activeTab" class="transparent">
              <v-tab-item>
                <v-container>
                  <v-row>
                    <v-col cols="10">
                      <BaseSelect v-model="selectedLoc" :label="'Seleccione una Licencia'" :items="locOptions" />
                    </v-col>
                    <v-col cols="2">
                      <TextButton :loading="loading" :disabled="loading || !selectedLoc" @click="handleSavePersonalLoc">
                        Vincular
                      </TextButton>
                    </v-col>
                    <v-col cols="12">
                      <LocsPersonalTable :locs-personal="locsPersonal" />
                    </v-col>
                  </v-row>
                </v-container>
              </v-tab-item>
              <v-tab-item>
                <v-container>
                  <v-row>
                    <v-col cols="10">
                      <BaseSelect v-model="selectedRemesa" :label="'Seleccione una Remesa'" :items="remesasOptions" />
                    </v-col>
                    <v-col cols="2">
                      <TextButton
                        :loading="loading"
                        :disabled="loading || !selectedRemesa"
                        @click="handleSavePersonalRemesa"
                      >
                        Vincular
                      </TextButton>
                    </v-col>
                    <v-col cols="12">
                      <RemesasPersonalTable :remesas-personal="remesasPersonal" :remesas="remesas" />
                    </v-col>
                  </v-row>
                </v-container>
              </v-tab-item>
            </v-tabs-items>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import BaseSelect from '../../base/BaseSelect.vue'
import DateAdapter from '@/utils/DateAdapter'
import { API_DATE_FORMAT } from '@/const'
import TextButton from '@/components/base/TextButton.vue'
import LocsPersonalTable from './LocsPersonalTable.vue'
import RemesasPersonalTable from './RemesasPersonalTable.vue'

export default {
  components: {
    BaseSelect,
    TextButton,
    LocsPersonalTable,
    RemesasPersonalTable,
  },

  props: {
    idPersonal: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      activeTab: 0,
      selectedLoc: null,
      selectedRemesa: null,
      loading: false,
    }
  },

  computed: {
    ...mapGetters('personalLoc', { locsPersonal: 'getPersonalLoc' }),
    ...mapGetters('licenciaOficialColectiva', { locs: 'getLocs' }),
    ...mapGetters('remesas', { remesas: 'getRemesas', remesasPersonal: 'getPersonalRemesas' }),

    locOptions() {
      return this.locs
        .map((loc) => ({
          title: `Licencia #${loc.noLicencia} - dada de alta el ${new DateAdapter(
            loc.fechaAlta,
            API_DATE_FORMAT
          ).toHumanReadable()}`,
          id: loc.idLoc,
        }))
        .filter((loc) => !this.locsPersonal.some(({ fk_idLoc }) => fk_idLoc === loc.id))
    },

    remesasOptions() {
      return this.remesas
        .map((remesa) => {
          const loc = this.locs.find((l) => l.idLoc === remesa.fk_idLoc)
          const numeroLicencia = loc?.noLicencia || 'N/A'

          return {
            title: `Remesa #${remesa.numOficio} - dada de alta el ${remesa.fechaAlta} - No. de LOC ${numeroLicencia}`,
            id: remesa.idRemesa,
          }
        })
        .filter((remesa) => !this.remesasPersonal.some(({ fk_idRemesa }) => fk_idRemesa === remesa.id))
    },
  },

  mounted() {
    this.fetchLocs()
    this.fetchPersonalLoc(this.idPersonal)
    this.fetchRemesas()
    this.fetchPersonalRemesas(this.idPersonal)
  },

  methods: {
    ...mapActions('personalLoc', ['fetchPersonalLoc', 'savePersonalLoc']),
    ...mapActions('licenciaOficialColectiva', ['fetchLocs']),
    ...mapActions('remesas', ['fetchRemesas', 'fetchPersonalRemesas', 'assignRemesaToPersonal']),

    async handleSavePersonalLoc() {
      this.loading = true
      await this.savePersonalLoc({ idPersonal: this.idPersonal, fk_idLoc: this.selectedLoc })
      await this.fetchPersonalLoc(this.idPersonal)
      this.selectedLoc = null
      this.loading = false
    },

    async handleSavePersonalRemesa() {
      this.loading = true
      await this.assignRemesaToPersonal({ idPersonal: this.idPersonal, idRemesa: this.selectedRemesa })
      this.selectedRemesa = null
      this.loading = false
    },
  },
}
</script>

<style scoped>
::v-deep .v-input__slot {
  border-radius: 12px !important;
  border: 1px solid rgba(var(--v-secondary-base-rgb), 0.25) !important;
  background: rgba(16, 16, 30, 0.35) !important;
  box-shadow: none !important;
  transition: all 0.3s ease !important;
}

::v-deep .v-input__slot:hover {
  border-color: rgba(var(--v-secondary-base-rgb), 0.45) !important;
}

::v-deep .v-input--is-focused .v-input__slot {
  border-color: rgba(var(--v-secondary-base-rgb), 0.7) !important;
  background: rgba(16, 16, 30, 0.5) !important;
}

::v-deep .v-input__control {
  background: transparent !important;
  border-radius: 12px;
  border: none !important;
  box-shadow: none !important;
}

::v-deep input {
  color: #e0e0e0 !important;
}

::v-deep .v-label,
::v-deep .v-input .v-icon {
  color: rgba(var(--v-secondary-base-rgb), 0.75) !important;
}

::v-deep .v-input--is-focused .v-label {
  color: var(--v-secondary-base) !important;
}

::v-deep .v-tabs {
  background: transparent !important;
}

::v-deep .v-tab {
  color: rgba(224, 224, 224, 0.7) !important;
  text-transform: none !important;
  font-weight: 600 !important;
  transition: all 0.3s ease;
}

::v-deep .v-tab:hover {
  color: var(--v-secondary-base) !important;
}

::v-deep .v-tab--active {
  color: var(--v-secondary-base) !important;
}

::v-deep .v-tabs-slider {
  background: var(--v-secondary-base) !important;
  height: 3px !important;
  border-radius: 3px 3px 0 0;
  box-shadow: 0 2px 8px rgba(var(--v-secondary-base-rgb), 0.5);
}

::v-deep .v-tabs-items {
  background: transparent !important;
}

::v-deep .v-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) !important;
  position: relative;
  overflow: hidden;
}

::v-deep .v-btn::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
  pointer-events: none;
}

::v-deep .v-btn:active::before {
  width: 300px;
  height: 300px;
}

::v-deep .v-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(var(--v-secondary-base-rgb), 0.6) !important;
}

.text-h5 {
  color: #ffffff !important;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

::v-deep .v-card__subtitle {
  color: rgba(224, 224, 224, 0.7) !important;
}
</style>
